<template>
  <div class="analytics-grid">
    <div class="analytics-card impressions">
      <div class="card-header">
        <h3><i class="fas fa-chart-bar"></i> Impressions & Plays</h3>
        <div class="card-actions">
          <button class="action-btn"><i class="fas fa-download"></i></button>
          <button class="action-btn"><i class="fas fa-ellipsis-v"></i></button>
        </div>
      </div>
      <Bar :data="impressionsPlaysData" :options="barOptions" />
    </div>

    <div class="analytics-card watch-time">
      <div class="card-header">
        <h3><i class="fas fa-hourglass-half"></i> Watch Time Analysis</h3>
        <div class="card-actions">
          <button class="action-btn"><i class="fas fa-download"></i></button>
          <button class="action-btn"><i class="fas fa-ellipsis-v"></i></button>
        </div>
      </div>
      <div class="watch-time-stats">
        <div class="stat-box">
          <span class="label">Total Watch Time</span>
          <span class="value">{{ formatWatchTime(currentPeriodData.watch_time?.total) }}</span>
          <span class="trend positive">
            <i class="fas fa-arrow-up"></i> 12%
          </span>
        </div>
        <div class="stat-box">
          <span class="label">Average Watch Time</span>
          <span class="value">{{ formatWatchTime(currentPeriodData.watch_time?.average) }}</span>
          <span class="trend negative">
            <i class="fas fa-arrow-down"></i> 3%
          </span>
        </div>
      </div>
    </div>

    <div class="analytics-card engagement-chart">
      <div class="card-header">
        <h3><i class="fas fa-chart-pie"></i> Engagement Distribution</h3>
        <div class="card-actions">
          <button class="action-btn"><i class="fas fa-download"></i></button>
          <button class="action-btn"><i class="fas fa-ellipsis-v"></i></button>
        </div>
      </div>
      <Doughnut :data="engagementData" :options="doughnutOptions" />
    </div>

    <div class="analytics-card audience">
      <div class="card-header">
        <h3><i class="fas fa-globe"></i> Audience Demographics</h3>
        <div class="card-actions">
          <button class="action-btn"><i class="fas fa-download"></i></button>
          <button class="action-btn"><i class="fas fa-ellipsis-v"></i></button>
        </div>
      </div>
      <div class="demographics-stats">
        <div class="demographic-item">
          <span class="label">Top Countries</span>
          <div class="country-list">
            <div class="country-item">
              <span class="country-name">United States</span>
              <span class="country-value">32%</span>
            </div>
            <div class="country-item">
              <span class="country-name">United Kingdom</span>
              <span class="country-value">18%</span>
            </div>
            <div class="country-item">
              <span class="country-name">Canada</span>
              <span class="country-value">12%</span>
            </div>
          </div>
        </div>
        <div class="demographic-item">
          <span class="label">Age Groups</span>
          <div class="age-groups">
            <div class="age-group">
              <span class="age-range">18-24</span>
              <div class="progress-bar">
                <div class="progress" style="width: 45%"></div>
              </div>
              <span class="age-value">45%</span>
            </div>
            <div class="age-group">
              <span class="age-range">25-34</span>
              <div class="progress-bar">
                <div class="progress" style="width: 30%"></div>
              </div>
              <span class="age-value">30%</span>
            </div>
            <div class="age-group">
              <span class="age-range">35-44</span>
              <div class="progress-bar">
                <div class="progress" style="width: 15%"></div>
              </div>
              <span class="age-value">15%</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="analytics-card revenue">
      <div class="card-header">
        <h3><i class="fas fa-dollar-sign"></i> Revenue Overview</h3>
        <div class="card-actions">
          <button class="action-btn"><i class="fas fa-download"></i></button>
          <button class="action-btn"><i class="fas fa-ellipsis-v"></i></button>
        </div>
      </div>
      <div class="revenue-stats">
        <div class="revenue-item">
          <span class="label">Ad Revenue</span>
          <span class="value">$2,450</span>
          <span class="trend positive">
            <i class="fas fa-arrow-up"></i> 8.5%
          </span>
        </div>
        <div class="revenue-item">
          <span class="label">Membership Revenue</span>
          <span class="value">$1,280</span>
          <span class="trend positive">
            <i class="fas fa-arrow-up"></i> 15.2%
          </span>
        </div>
        <div class="revenue-item">
          <span class="label">Total Revenue</span>
          <span class="value">$3,730</span>
          <span class="trend positive">
            <i class="fas fa-arrow-up"></i> 10.8%
          </span>
        </div>
      </div>
    </div>

    <div class="analytics-card devices">
      <div class="card-header">
        <h3><i class="fas fa-mobile-alt"></i> Device Distribution</h3>
        <div class="card-actions">
          <button class="action-btn"><i class="fas fa-download"></i></button>
          <button class="action-btn"><i class="fas fa-ellipsis-v"></i></button>
        </div>
      </div>
      <div class="device-stats">
        <div class="device-item">
          <span class="device-name">Mobile</span>
          <div class="progress-bar">
            <div class="progress" style="width: 65%"></div>
          </div>
          <span class="device-value">65%</span>
        </div>
        <div class="device-item">
          <span class="device-name">Desktop</span>
          <div class="progress-bar">
            <div class="progress" style="width: 25%"></div>
          </div>
          <span class="device-value">25%</span>
        </div>
        <div class="device-item">
          <span class="device-name">Tablet</span>
          <div class="progress-bar">
            <div class="progress" style="width: 10%"></div>
          </div>
          <span class="device-value">10%</span>
        </div>
      </div>
    </div>

    <div class="analytics-card content-performance">
      <div class="card-header">
        <h3><i class="fas fa-film"></i> Content Performance</h3>
        <div class="card-actions">
          <button class="action-btn"><i class="fas fa-download"></i></button>
          <button class="action-btn"><i class="fas fa-ellipsis-v"></i></button>
        </div>
      </div>
      <div class="content-stats">
        <div class="content-item">
          <div class="content-info">
            <span class="content-title">Top Performing Video</span>
            <span class="content-views">245,678 views</span>
          </div>
          <div class="content-metrics">
            <div class="metric">
              <span class="metric-label">Watch Time</span>
              <span class="metric-value">12.5 min</span>
            </div>
            <div class="metric">
              <span class="metric-label">Engagement</span>
              <span class="metric-value">78%</span>
            </div>
          </div>
        </div>
        <div class="content-item">
          <div class="content-info">
            <span class="content-title">Most Shared</span>
            <span class="content-views">12,456 shares</span>
          </div>
          <div class="content-metrics">
            <div class="metric">
              <span class="metric-label">Comments</span>
              <span class="metric-value">1,234</span>
            </div>
            <div class="metric">
              <span class="metric-label">Likes</span>
              <span class="metric-value">8,765</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="analytics-card traffic-sources">
      <div class="card-header">
        <h3><i class="fas fa-traffic-light"></i> Traffic Sources</h3>
        <div class="card-actions">
          <button class="action-btn"><i class="fas fa-download"></i></button>
          <button class="action-btn"><i class="fas fa-ellipsis-v"></i></button>
        </div>
      </div>
      <div class="traffic-stats">
        <div class="traffic-item">
          <div class="traffic-source">
            <i class="fab fa-youtube"></i>
            <span>YouTube Search</span>
          </div>
          <div class="traffic-metrics">
            <span class="traffic-value">45%</span>
            <div class="progress-bar">
              <div class="progress" style="width: 45%"></div>
            </div>
          </div>
        </div>
        <div class="traffic-item">
          <div class="traffic-source">
            <i class="fas fa-external-link-alt"></i>
            <span>External Links</span>
          </div>
          <div class="traffic-metrics">
            <span class="traffic-value">25%</span>
            <div class="progress-bar">
              <div class="progress" style="width: 25%"></div>
            </div>
          </div>
        </div>
        <div class="traffic-item">
          <div class="traffic-source">
            <i class="fas fa-share-alt"></i>
            <span>Social Media</span>
          </div>
          <div class="traffic-metrics">
            <span class="traffic-value">20%</span>
            <div class="progress-bar">
              <div class="progress" style="width: 20%"></div>
            </div>
          </div>
        </div>
        <div class="traffic-item">
          <div class="traffic-source">
            <i class="fas fa-home"></i>
            <span>Channel Page</span>
          </div>
          <div class="traffic-metrics">
            <span class="traffic-value">10%</span>
            <div class="progress-bar">
              <div class="progress" style="width: 10%"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="analytics-card engagement-metrics">
      <div class="card-header">
        <h3><i class="fas fa-chart-line"></i> Engagement Metrics</h3>
        <div class="card-actions">
          <button class="action-btn"><i class="fas fa-download"></i></button>
          <button class="action-btn"><i class="fas fa-ellipsis-v"></i></button>
        </div>
      </div>
      <div class="engagement-stats">
        <div class="engagement-item">
          <div class="engagement-info">
            <span class="engagement-label">Average View Duration</span>
            <span class="engagement-value">4:32</span>
          </div>
          <div class="engagement-trend positive">
            <i class="fas fa-arrow-up"></i> 12%
          </div>
        </div>
        <div class="engagement-item">
          <div class="engagement-info">
            <span class="engagement-label">Click-Through Rate</span>
            <span class="engagement-value">3.8%</span>
          </div>
          <div class="engagement-trend positive">
            <i class="fas fa-arrow-up"></i> 5%
          </div>
        </div>
        <div class="engagement-item">
          <div class="engagement-info">
            <span class="engagement-label">Comments per Video</span>
            <span class="engagement-value">156</span>
          </div>
          <div class="engagement-trend negative">
            <i class="fas fa-arrow-down"></i> 2%
          </div>
        </div>
        <div class="engagement-item">
          <div class="engagement-info">
            <span class="engagement-label">Shares per Video</span>
            <span class="engagement-value">89</span>
          </div>
          <div class="engagement-trend positive">
            <i class="fas fa-arrow-up"></i> 8%
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Bar, Doughnut } from 'vue-chartjs';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

export default {
  name: 'AnalyticsGrid',
  components: { Bar, Doughnut },
  props: {
    currentPeriodData: {
      type: Object,
      default: () => ({})
    },
    impressionsPlaysData: {
      type: Object,
      default: () => ({})
    },
    engagementData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      barOptions: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true
          }
        }
      },
      doughnutOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom'
          }
        }
      }
    }
  },
  methods: {
    formatWatchTime(time) {
      if (!time) return '0:00';
      let result = '';
      if (time.hours > 0) result += `${time.hours}h `;
      if (time.minutes > 0) result += `${time.minutes}m `;
      result += `${time.seconds}s`;
      return result.trim();
    }
  }
}
</script>

<style scoped>
.analytics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 1.5rem;
}

.analytics-card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  height: 400px;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
}

.analytics-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 12px rgba(0, 0, 0, 0.1);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.card-header h3 {
  margin: 0;
  color: #1e293b;
  font-size: 1.1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.card-header h3 i {
  color: #2563eb;
}

.card-actions {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn:hover {
  background: #e2e8f0;
  color: #1e293b;
}

.watch-time-stats {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-top: 1rem;
}

.stat-box {
  background: #f8fafc;
  padding: 1.25rem;
  border-radius: 12px;
  position: relative;
  transition: all 0.2s ease;
}

.stat-box:hover {
  background: #f1f5f9;
}

.stat-box .label {
  font-size: 0.875rem;
  color: #64748b;
  display: block;
  margin-bottom: 0.5rem;
}

.stat-box .value {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  display: block;
}

.trend {
  position: absolute;
  top: 1.25rem;
  right: 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.trend.positive {
  color: #10b981;
}

.trend.negative {
  color: #ef4444;
}

.demographics-stats {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-top: 1rem;
}

.demographic-item {
  background: #f8fafc;
  padding: 1.25rem;
  border-radius: 12px;
}

.country-list {
  display: grid;
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.country-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
}

.country-name {
  color: #1e293b;
  font-weight: 500;
}

.country-value {
  color: #64748b;
  font-weight: 500;
}

.age-groups {
  display: grid;
  gap: 1rem;
  margin-top: 0.75rem;
}

.age-group {
  display: grid;
  grid-template-columns: 80px 1fr 60px;
  gap: 1rem;
  align-items: center;
}

.age-range {
  color: #1e293b;
  font-weight: 500;
}

.progress-bar {
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
}

.progress {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #2563eb);
  border-radius: 4px;
}

.age-value {
  color: #64748b;
  font-weight: 500;
  text-align: right;
}

.revenue-stats {
  display: grid;
  gap: 1rem;
  margin-top: 1rem;
}

.revenue-item {
  background: #f8fafc;
  padding: 1.25rem;
  border-radius: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.revenue-item .value {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
}

.device-stats {
  display: grid;
  gap: 1rem;
  margin-top: 1rem;
}

.device-item {
  display: grid;
  grid-template-columns: 100px 1fr 60px;
  gap: 1rem;
  align-items: center;
}

.device-name {
  color: #1e293b;
  font-weight: 500;
}

.device-value {
  color: #64748b;
  font-weight: 500;
  text-align: right;
}

.content-stats {
  display: grid;
  gap: 1rem;
  margin-top: 1rem;
}

.content-item {
  background: #f8fafc;
  padding: 1.25rem;
  border-radius: 12px;
}

.content-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.content-title {
  font-weight: 600;
  color: #1e293b;
}

.content-views {
  color: #64748b;
  font-weight: 500;
}

.content-metrics {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.metric {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.metric-label {
  color: #64748b;
  font-size: 0.875rem;
}

.metric-value {
  font-weight: 600;
  color: #1e293b;
}

.traffic-stats {
  display: grid;
  gap: 1rem;
  margin-top: 1rem;
}

.traffic-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.traffic-source {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #1e293b;
  font-weight: 500;
}

.traffic-source i {
  font-size: 1.25rem;
  color: #64748b;
}

.traffic-metrics {
  display: flex;
  align-items: center;
  gap: 1rem;
  min-width: 120px;
}

.traffic-value {
  min-width: 40px;
  text-align: right;
  color: #64748b;
  font-weight: 500;
}

.engagement-stats {
  display: grid;
  gap: 1rem;
  margin-top: 1rem;
}

.engagement-item {
  background: #f8fafc;
  padding: 1.25rem;
  border-radius: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.engagement-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.engagement-label {
  color: #64748b;
  font-size: 0.875rem;
}

.engagement-value {
  font-weight: 600;
  color: #1e293b;
  font-size: 1.25rem;
}

.engagement-trend {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 500;
}

.engagement-trend.positive {
  color: #10b981;
}

.engagement-trend.negative {
  color: #ef4444;
}

@media (max-width: 768px) {
  .analytics-grid {
    grid-template-columns: 1fr;
  }
  
  .content-metrics {
    grid-template-columns: 1fr;
  }
  
  .traffic-metrics {
    min-width: 100px;
  }
  
  .age-group,
  .device-item {
    grid-template-columns: 80px 1fr 50px;
  }
}
</style> 